import type { ReactNode } from "react";
import styles from "./Button.module.css";

interface ButtonProps {
  children: ReactNode;
  /**
   * Visual variant:
   * - `default` (Figma node 17:7200) — dark action-default fill,
   *   border-default outline, foreground-default text at regular
   *   weight. The standard chrome button (e.g. the "Show list" toggle
   *   sitting over the map).
   * - `inverse` (Figma node 183:10811) — light inverse fill with
   *   foreground-inverse text at medium weight. Used when the button
   *   sits over the dark page surface and needs to read as the
   *   primary action (e.g. "Show map" on the list view).
   * - `elevated` (Figma modal action, node 272:10575) — action-elevated
   *   fill with border-default outline at the taller --button-md-plus
   *   height. Used for the "Send / Close" action inside the form
   *   modals.
   */
  variant?: "default" | "inverse" | "elevated";
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  type?: "button" | "submit";
  "aria-label"?: string | undefined;
  className?: string | undefined;
}

export function Button({
  children,
  variant = "default",
  onClick,
  disabled,
  type = "button",
  "aria-label": ariaLabel,
  className,
}: ButtonProps) {
  return (
    <button
      type={type}
      className={[styles.button, styles[variant], className]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}
